import type { AllowanceResponse, MyMembershipResponse, PlansResponse } from '@league/shared/contract';
import type { DirectorLifecycleResult } from '@league/shared/league-lifecycle';
import { formatMoney } from '@league/shared/money';
import { Button, Icon, LINK_CLASS } from '@league/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';

import { useLeagueCreationAccess } from './useLeagueCreationAccess';
import { apiFetch } from '../../lib/api';
import { DirectorLifecycleBadge } from './DirectorLifecycleBadge';
import { useDirectorLifecycle } from './useDirectorLifecycle';

interface League {
  id: string;
  name: string;
  status: string;
  enrolled: number;
  sessionCount: number;
  lifecycleSessions: { startsAt: string; status: string; hasSavedSeating: boolean }[];
}

export function LeaguesPage({ archived = false }: { archived?: boolean }) {
  const navigate = useNavigate();
  const creation = useLeagueCreationAccess(!archived);

  const leagues = useQuery({
    queryKey: ['director-leagues'],
    queryFn: () => apiFetch<{ leagues: League[] }>('/api/director/leagues'),
  });

  /*
    Only on the current list. The archived view is a record of what she has finished, and nothing
    there counts against her allowance or costs her anything.
  */
  const allowanceQuery = useQuery({
    queryKey: ['billing', 'allowance'],
    queryFn: () => apiFetch<AllowanceResponse>('/api/billing/allowance'),
    enabled: !archived,
  });

  const catalogue = useQuery({
    queryKey: ['plans'],
    queryFn: () => apiFetch<PlansResponse>('/api/public/plans'),
    enabled: !archived,
  });

  const allowance = allowanceQuery.data?.allowance ?? null;
  const membership = useQuery({
    queryKey: ['billing', 'me'],
    queryFn: () => apiFetch<MyMembershipResponse>('/api/billing/me'),
    enabled: !archived,
  });
  const legacyChargeWarning = membership.data?.renewal?.mayWrite !== false && membership.data?.cancellation?.mayWrite !== false && allowance?.billingMode === 'legacy_usage'
    && ['active', 'past_due'].includes(membership.data?.subscription?.status ?? '');
  const extraPrice = catalogue.data?.plans.find((plan) => plan.key === 'extra_league_monthly');

  const allRows = leagues.data?.leagues ?? [];
  const lifecycles = useDirectorLifecycle(
    allRows.map((league) => ({ status: league.status, sessions: league.lifecycleSessions })),
  );
  const rows = allRows.flatMap((league, index) => {
    const lifecycle = lifecycles[index]!;
    return (lifecycle.state === 'completed') === archived ? [{ league, lifecycle }] : [];
  });

  return (
    <div className={`mx-auto flex flex-col gap-6 px-6 py-10 ${archived ? 'max-w-3xl' : 'max-w-5xl'}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className={`font-display leading-none ${archived ? 'text-2xl' : 'text-3xl'}`}>
            {archived ? 'Past leagues' : 'Your Leagues'}
          </h1>
          {!archived ? (
            <p className="mt-3 text-[14px] text-ink-2">Everything you need to manage your current leagues starts here.</p>
          ) : null}
        </div>
      </div>

      {archived ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-ink-2">
          <Link to="/director/leagues" className={LINK_CLASS}>My Leagues</Link>
        </div>
      ) : null}

      {leagues.isError ? <p role="alert">Could not load your leagues. Please try again.</p> : null}

      {leagues.isPending ? <p className="text-sm text-ink-3">Loading…</p> : null}

      {!leagues.isPending && !leagues.isError && rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-light px-6 py-10 text-center">
          <p className="text-[15px]">
            {archived ? 'No past leagues yet.' : allRows.length === 0 ? 'No leagues yet.' : 'No current leagues.'}
          </p>
          {!archived ? (
            <>
              <p className="mt-1 text-[13px] text-ink-3">
                Create a league to start planning your sessions and inviting players.
              </p>
            </>
          ) : null}
        </div>
      ) : null}

      {rows.length > 0 ? (
        <ul className={archived
          ? 'flex flex-col divide-y divide-light rounded-md border border-light bg-paper'
          : 'flex flex-col gap-4'}>
          {rows.map(({ league, lifecycle }) => (
            <li key={league.id}>
              {!archived ? <ManagementCard league={league} lifecycle={lifecycle} /> : <Link
                to={`/director/leagues/${league.id}`}
                className="flex items-start justify-between gap-4 px-5 py-4 transition-colors hover:bg-sand"
              >
                <span className="min-w-0 font-medium">{league.name}</span>
                <div className="shrink-0">
                  <DirectorLifecycleBadge lifecycle={lifecycle} leagueStatus={league.status} />
                </div>
              </Link>}
            </li>
          ))}
        </ul>
      ) : null}
      {!archived ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <section className="rounded-md border border-light bg-sand p-6">
            <h2 className="font-display text-2xl leading-tight">Planning another league?</h2>
            {/*
              WHERE SHE STANDS AGAINST WHAT SHE INCLUDES, in one line and only when there is a
              number to state. Unlimited memberships say nothing at all rather than "unlimited of
              unlimited", and while the answer is still loading it says the plain sentence it always
              said — a figure that appears a beat later beside a button is the kind of movement that
              makes somebody re-read the screen.
            */}
            <p className="mt-2 text-[13px] text-ink-2">
              {creation.trialNote ?? (allowance && allowance.included !== null
                ? `You’re using ${allowance.active} of your ${allowance.purchasedAllowance ?? allowance.included} league spaces.`
                : allowance ? `You’re using ${allowance.active} league spaces. Your membership includes unlimited league spaces.` : 'Loading your league allowance…')}
            </p>
            {creation.trialNote ? null : creation.full ? <p className="mt-1.5 text-[13px] text-ink-2">{creation.fullNote}</p> : legacyChargeWarning && allowance && allowance.included !== null && allowance.active >= allowance.included && extraPrice ? (
              <p className="mt-1.5 text-[13px] text-ink-2">
                The next one adds {formatMoney(extraPrice.amountCents, extraPrice.currency)} a month,
                until it finishes.
              </p>
            ) : null}
            {creation.ready && !creation.writable ? <Link to="/director/settings" className={`mt-3 block w-fit text-[13px] ${LINK_CLASS}`}>Review your membership to create a league.</Link> : null}
            <Button disabled={!creation.mayCreate} className="mt-5" onClick={() => navigate('/director/leagues/new')}>Create League</Button>
          </section>
          <section className="rounded-md border border-light bg-paper p-6">
            <h2 className="font-display text-2xl leading-tight">Looking for a past league?</h2>
            <p className="mt-2 text-[13px] text-ink-2">View your past leagues.</p>
            <Link to="/director/leagues/archived" className={`mt-5 inline-block text-[14px] ${LINK_CLASS}`}>
              Past leagues
            </Link>
          </section>
        </div>
      ) : null}
    </div>
  );
}


function ManagementCard({ league, lifecycle }: { league: League; lifecycle: DirectorLifecycleResult }) {
  const details = useQuery({
    queryKey: ['league', league.id],
    queryFn: () => apiFetch<{ league: { venueName: string | null } }>(`/api/leagues/${league.id}`),
  });
  const venueName = details.data?.league.venueName;
  const [locationName, ...locationParts] = venueName?.split(',') ?? [];
  const locationDetail = locationParts.join(',').trim();

  return (
    <Link
      to={`/director/leagues/${league.id}`}
      aria-label={`Manage League: ${league.name}`}
      className="block rounded-md border border-light bg-paper p-6 transition-colors hover:border-control-border"
    >
      <div className="flex items-start justify-between gap-4">
        <h2 className="min-w-0 break-words font-display text-[28px] leading-tight">{league.name}</h2>
        <div className="shrink-0"><DirectorLifecycleBadge lifecycle={lifecycle} leagueStatus={league.status} /></div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
        <div className="flex min-w-0 basis-full flex-wrap items-start gap-x-7 gap-y-4 sm:flex-1 sm:basis-0">
          <div className="flex items-start gap-2.5">
            <Icon name="players" size={24} className="text-ink-3" />
            <div>
              <p className="tabular text-2xl font-medium leading-none">{league.enrolled}</p>
              <p className="mt-1 text-[12px] text-ink-2">players</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <Icon name="league" size={24} className="text-ink-3" />
            <div>
              <p className="tabular text-2xl font-medium leading-none">{league.sessionCount}</p>
              <p className="mt-1 text-[12px] text-ink-2">sessions</p>
            </div>
          </div>
          {venueName ? (
            <div className="flex min-w-0 max-w-xs items-start gap-2.5">
              <Icon name="market" size={24} className="text-ink-3" />
              <div>
                <p className="text-base font-medium leading-snug">{locationName?.trim()}</p>
                {locationDetail ? <p className="mt-1 text-[12px] text-ink-2">{locationDetail}</p> : null}
              </div>
            </div>
          ) : null}
        </div>
        <span className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-md bg-deep px-5 text-sm font-medium text-on-deep">
          Manage League <Icon name="chevronRight" size={16} />
        </span>
      </div>
    </Link>
  );
}
